import { datadogRum } from "@datadog/browser-rum";

import version from "@/base/version";
import useImmediateEffect from "@/hooks/useImmediateEffect";
import getApplicationEnvironment from "@/utils/getApplicationEnvironment";

import {
  getSampleRate,
  isRumEnabled,
  versionForDatadog,
} from "@/monitoring/utils";
import useDatadogUserProperties from "../useDatadogUserProperties";

const RUMTracking = () => {
  useImmediateEffect(() => {
    if (!globalThis.window) return;

    const { environment } = getApplicationEnvironment();
    if (isRumEnabled()) {
      datadogRum.setGlobalContext({
        gitRepository: version.gitRepository,
      });

      datadogRum.init({
        applicationId: process.env["NEXT_PUBLIC_DATADOG_APP_ID"] ?? "",
        clientToken: process.env["NEXT_PUBLIC_DATADOG_TOKEN"] ?? "",
        sessionSampleRate: getSampleRate(),
        env: environment,
        site: "datadoghq.com",
        service: "lyceum",
        version: versionForDatadog,
        // `sessionReplaySampleRate` is a percentage of `sampleRate`, see:
        // https://docs.datadoghq.com/real_user_monitoring/guide/sampling-browser-plans/
        sessionReplaySampleRate: 100,
        // Out of caution for user privacy, we obscure all text by default for
        // Datadog session replay recordings. To override on a per-element basis,
        // provide the prop / attribute `data-dd-privacy='mask-user-input'`. See:
        // https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/
        defaultPrivacyLevel: "mask",
        // trackUserInteractions enables the automatic collection of user clicks in your application
        trackUserInteractions: true,
        // trackResources enables collection of resource events
        trackResources: true,
        // trackLongTasks enables collection of long task events
        trackLongTasks: true,
        /*
         * allowedTracingUrls is the list of request URLs used to inject tracing headers
         * match format is needed to connect traces with the next.js app backend. But, it may
         * require dd-trace config updates in the monolith.
         */
        allowedTracingUrls: [
          // Production.
          { match: "https://newsela.com", propagatorTypes: ["tracecontext"] },
          // Staging and lower envs.
          {
            match: /https:\/\/.*\.newseladev\.com\/.*/,
            propagatorTypes: ["tracecontext"],
          },
        ],
      });
    }
  }, []);

  const datadogUserProperties = useDatadogUserProperties();

  useImmediateEffect(() => {
    if (!globalThis.window) return;
    datadogRum.setUser(datadogUserProperties);
  }, [datadogUserProperties]);

  return null;
};

export default RUMTracking;
